import React, { useEffect, useRef, useState } from 'react';
import { Form, Modal, Button } from 'react-bootstrap';
import API from '../services/api';
import { keyFor } from '../utils/expenseCategoryMatch';


// Type-to-filter picker for expense categories, styled like SearchSelect. With `allowCreate`, the last option adds
// what was typed as a new category — the server refuses an exact duplicate (that one is simply picked) and asks to
// confirm a name that looks like an existing one (a modal offers the look-alikes or "agregar de todas formas").
// With `clearLabel`, an empty value means "all" (used as a filter) and a × clears it.
const ExpenseCategoryPicker = ({ categories, value, onChange, onCreated, allowCreate = false, placeholder = 'Escribe para buscar...', clearLabel, required }) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [similar, setSimilar] = useState(null); // { name, list } while asking to confirm
  const inputRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const typed = query.trim();
  const typedKey = keyFor(typed);
  const matches = typed && typed !== value
    ? categories.filter(c => keyFor(c.name).includes(typedKey))
    : categories;
  const exact = categories.find(c => keyFor(c.name) === typedKey);
  const canAdd = allowCreate && typedKey && !exact;
  const options = [...matches.map(c => ({ type: 'pick', name: c.name })), ...(canAdd ? [{ type: 'add', name: typed }] : [])];
  // The blur handler runs after a delay, by which time an add may have finished: it must see the state as it is then.
  const latest = useRef({});
  latest.current = { value, exact, similar, busy };

  const pick = (name) => {
    onChange(name);
    setQuery(name);
    setOpen(false);
    setNote('');
  };

  const create = async (name, confirmSimilar = false) => {
    latest.current.busy = true;
    setBusy(true);
    setNote('');
    try {
      const res = await API.post('/expense-categories', { name, confirmSimilar });
      setSimilar(null);
      onCreated?.(res.data);
      pick(res.data.name);
      setNote(`Categoría "${res.data.name}" agregada.`);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'DUPLICATE' && data.existing) {
        setSimilar(null);
        pick(data.existing.name);
        setNote(`"${data.existing.name}" ya existía, se seleccionó.`);
      } else if (data.code === 'SIMILAR') {
        setOpen(false);
        setSimilar({ name, list: data.similar || [] });
      } else {
        setNote(data.message || 'No se pudo agregar la categoría.');
      }
    } finally {
      setBusy(false);
    }
  };

  const choose = (option) => (option.type === 'add' ? create(option.name) : pick(option.name));

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(i => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && options[active]) { e.preventDefault(); choose(options[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  // Leaving the field: an exact match (any case/accents) counts as picking it; anything else goes back to the value.
  const handleBlur = () => setTimeout(() => {
    setOpen(false);
    const { value: current, exact: match, similar: asking, busy: adding } = latest.current;
    if (adding || asking) return; // an add in progress picks (or asks about) the name itself
    if (match && match.name !== current) pick(match.name);
    else if (!match) setQuery(current || '');
  }, 150);

  return (
    <div className="search-select">
      <div className="position-relative">
        <Form.Control
          ref={inputRef}
          value={query}
          placeholder={value === '' && clearLabel ? clearLabel : placeholder}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onFocus={(e) => { setOpen(true); setActive(0); e.target.select(); }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          readOnly={busy}
          style={clearLabel && value ? { paddingRight: '2rem' } : undefined}
        />
        {/* Keeps the browser's "required" check: the visible input holds free text, this holds the picked name. */}
        {required && <input tabIndex={-1} aria-hidden="true" required value={value || ''} onChange={() => {}} className="search-select-required" />}
        {clearLabel && value && (
          <button type="button" className="search-select-clear" aria-label="Quitar filtro" onMouseDown={(e) => { e.preventDefault(); pick(''); }}>
            <i className="bi bi-x-lg" />
          </button>
        )}
      </div>
      {open && (
        <div className="search-select-menu">
          {clearLabel && (
            <div className={`search-select-item${!value && !typed ? ' active' : ''}`} onMouseDown={() => pick('')}>{clearLabel}</div>
          )}
          {options.length === 0 && <div className="search-select-empty">Sin resultados</div>}
          {options.map((o, i) => (
            <div
              key={`${o.type}:${o.name}`}
              className={`search-select-item${o.type === 'add' ? ' search-select-add' : ''}${i === active ? ' active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={() => choose(o)}
            >
              {o.type === 'add' ? <><i className="bi bi-plus-lg me-1" />Agregar categoría “{o.name}”</> : o.name}
            </div>
          ))}
        </div>
      )}
      {note && <div className="small mt-1" style={{ color: 'var(--mm-muted)' }}>{note}</div>}

      <Modal show={!!similar} onHide={() => { setSimilar(null); setQuery(value || ''); }} centered>
        <Modal.Header closeButton><Modal.Title>¿Categoría repetida?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="mb-2">Ya hay {similar?.list.length === 1 ? 'una categoría parecida' : 'categorías parecidas'} a “{similar?.name}”:</p>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {similar?.list.map(c => (
              <Button key={c._id} size="sm" variant="outline-primary" onClick={() => { setSimilar(null); pick(c.name); }}>
                Usar “{c.name}”
              </Button>
            ))}
          </div>
          <p className="small text-muted mt-3 mb-0">Usa la existente si es lo mismo; así los reportes no quedan divididos entre dos nombres.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setSimilar(null); setQuery(value || ''); }}>Cancelar</Button>
          <Button variant="primary" disabled={busy} onClick={() => create(similar.name, true)}>Agregar “{similar?.name}” de todas formas</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExpenseCategoryPicker;
